// @ts-nocheck
import "./style.scss";

import UnsupportedCurrencyFooter from "components/swap/UnsupportedCurrencyFooter";
import useActiveWeb3React from "hooks/useActiveWeb3React";
import { useCallback, useState } from "react";
import ReactGA from "react-ga4";
import { RouteComponentProps } from "react-router-dom";
import {
  useDerivedMintInfo,
  useMintActionHandlers,
  useMintState
} from "state/mint/hooks";
import useClubManager from "hooks/useClubManager";
import { useSingleCallResult } from 'lib/hooks/multicall'
import { BigNumber } from "@ethersproject/bignumber";
import { TransactionResponse } from "@ethersproject/providers";
import { Currency, CurrencyAmount, Percent } from "@uniswap/sdk-core";

import { AutoColumn } from "../../components/Column";
import CurrencyInputPanelAsset from "../../components/CurrencyInputPanel/asset";
import CurrencyInputPanelLimited from "../../components/CurrencyInputPanel/limited";
import TransactionConfirmationModal, { ConfirmationModalContent } from "../../components/TransactionConfirmationModal";
import {
  AMM_VAULT_ADDRESSES,
  ASSET_VAULT_ADDRESSES
} from "../../constants/addresses";
import { DEFAULT_TXN_DISMISS_MS } from "../../constants/misc";
import { WRAPPED_NATIVE_CURRENCY } from "../../constants/tokens";
import { useCurrency } from "../../hooks/Tokens";
import {
  ApprovalState,
  useApproveCallback
} from "../../hooks/useApproveCallback";
import { useVaultManagerContract } from "../../hooks/useContract";
import { useIsSwapUnsupported } from "../../hooks/useIsSwapUnsupported";
import { useUSDCValue } from "../../hooks/useUSDCPrice";
import { useV3PositionFromTokenId } from "../../hooks/useV3Positions";
import {
  useAddPopup,
  useWalletModalToggle
} from "../../state/application/hooks";
import { Field } from "../../state/mint/v3/actions";
import { TransactionType } from "../../state/transactions/actions";
import { useTransactionAdder } from "../../state/transactions/hooks";
import { useCurrencyBalance } from "../../state/wallet/hooks";
import { ThemedText } from "../../theme";
import { currencyId } from "../../utils/currencyId";
import { maxAmountSpend } from "../../utils/maxAmountSpend";
import {
  DynamicSection,
  HeaderTabs,
  PageWrapper,
  ResponsiveTwoColumns,
  ScrollablePage,
  Wrapper
} from "./styled";
import useVaultManager from "hooks/useVaultManager";

const DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE = new Percent(50, 10_000)

export default function AddLiquidity({
  match: {
    params: { currencyIdA, currencyIdB, feeAmount: feeAmountFromUrl, tokenId },
  },
  history,
  }: RouteComponentProps<{ currencyIdA?: string; currencyIdB?: string; feeAmount?: string; tokenId?: string }>) {
  const { account, chainId, library } = useActiveWeb3React()
  const pool = window.localStorage.getItem('SELECT_POOL') || 'Ubeswap'
  const vaultManager = useVaultManagerContract(pool.toLowerCase(), true)
  const addPopup = useAddPopup()
  // check for existing position if tokenId in url
  const { position: existingPositionDetails, loading: positionLoading } = useV3PositionFromTokenId(
    tokenId ? BigNumber.from(tokenId) : undefined
  )
  const hasExistingPosition = !!existingPositionDetails && !positionLoading

  const baseCurrency = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)
  // prevent an error if they input ETH/WETH
  const quoteCurrency =
    baseCurrency && currencyB && baseCurrency.wrapped.equals(currencyB.wrapped) ? undefined : currencyB

  // mint state
  const { independentField, typedValue } = useMintState();

  const {
    dependentField,
    currencies,
    currencyBalances,
    parsedAmounts,
    noLiquidity,
    totalSupply,
    error,
  } = useDerivedMintInfo(baseCurrency ?? undefined, currencyB ?? undefined, 'SELECT_POOL')
  const { onFieldAInput, onFieldBInput } =
  useMintActionHandlers(noLiquidity)
  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm
  const [waiting, setWaiting] = useState<boolean>(false)
  const [invalidA, setInvalidA] = useState<boolean>(false)
  const [invalidB, setInvalidB] = useState<boolean>(false)
  const { supplyAllowance } = useClubManager();
  const { capitalInUSD } = useVaultManager(currencies[Field.CURRENCY_A]?.address, (parsedAmounts[Field.CURRENCY_A])?.quotient.toString() ?? "0");
  const [txHash, setTxHash] = useState<string>('')
  // console.log(capitalInUsd);
  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toExact() ?? '',
  }

  const usdcValues = {
    [Field.CURRENCY_A]: useUSDCValue(parsedAmounts[Field.CURRENCY_A]),
    [Field.CURRENCY_B]: useUSDCValue(parsedAmounts[Field.CURRENCY_B]),
  }

  const [approval, approveCallback] = useApproveCallback(parsedAmounts[Field.CURRENCY_A], chainId ? AMM_VAULT_ADDRESSES[chainId][pool.toLowerCase()] : undefined)
  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )
  const assetMax = useCurrencyBalance(ASSET_VAULT_ADDRESSES[chainId!], currencies[Field.CURRENCY_B] ?? undefined);
  const addTransaction = useTransactionAdder()
  const requestedAmount = useSingleCallResult(vaultManager, 'getQuote', [(parsedAmounts[Field.CURRENCY_A])?.quotient.toString(), baseCurrency ? currencyId(baseCurrency) : null, quoteCurrency ? currencyId(quoteCurrency) : null]).result?.[0]

  async function approveToken() {
    try {
      setWaiting(true);
      await approveCallback(); 
    } catch (error) {
      console.log(error);
    }
    setWaiting(false);
  }

  async function onAdd() {
    if(waiting) return;
    if (!chainId || !library || !account) return

    if (!baseCurrency || !quoteCurrency) {
      return
    }
    if (vaultManager && account) {
      try {
        setWaiting(true);
        await vaultManager.addLiquidity(currencyId(baseCurrency), (parsedAmounts[Field.CURRENCY_A])?.quotient.toString(), currencyId(quoteCurrency), requestedAmount)
          .then(async (response: TransactionResponse) => {
            setTxHash(response.hash)
            addTransaction(response, {
              type: TransactionType.ADD_LIQUIDITY_V3_POOL,
              baseCurrencyId: currencyId(baseCurrency),
              quoteCurrencyId: currencyId(quoteCurrency),
              createPool: Boolean(noLiquidity),
              expectedAmountBaseRaw: parsedAmounts[Field.CURRENCY_A]?.quotient?.toString() ?? '0',
              expectedAmountQuoteRaw: parsedAmounts[Field.CURRENCY_B]?.quotient?.toString() ?? '0',
              feeAmount: 3000,
            })
            setShowConfirm(true)
            await response.wait();
            setAttemptingTxn(false)
            setWaiting(false);
          })

        ReactGA.event({
          category: 'Liquidity',
          action: 'Add',
          label: [currencies[Field.CURRENCY_A]?.symbol, currencies[Field.CURRENCY_B]?.symbol].join('/'),
        })
      }
      catch (error) {

        setWaiting(false);
        console.error('Failed to send transaction', error)
        if(error?.error) {
          addPopup(
            {
              reason: error?.error.message
            },
            Date.now(),
            DEFAULT_TXN_DISMISS_MS
          )
        }
        setAttemptingTxn(false)
        // we only care if the error is something _other_ than the user rejected the tx
        if (error?.code !== 4001) {
          console.error(error)
        }
      }
    }
  }


  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          currencyIdNew === 'ETH' ||
          (chainId !== undefined && currencyIdNew === WRAPPED_NATIVE_CURRENCY[chainId]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (currencyIdOther === 'ETH' ||
            (chainId !== undefined && currencyIdOther === WRAPPED_NATIVE_CURRENCY[chainId]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleCurrencyASelect = useCallback(
    (currencyANew: Currency) => {
      const [idA, idB] = handleCurrencySelect(currencyANew, currencyIdB)
      if (idB === undefined) {
        history.push(`/add/${idA}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleCurrencySelect, currencyIdB, history]
  )

  const handleCurrencyBSelect = useCallback(
    (currencyBNew: Currency) => {
      const [idB, idA] = handleCurrencySelect(currencyBNew, currencyIdA)
      if (idA === undefined) {
        history.push(`/add/0x0/${idB}`)
      } else {
        history.push(`/add/${idA}/${idB}`)
      }
    },
    [handleCurrencySelect, currencyIdA, history]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
      // dont jump to pool page if creating
      history.push('/pool')
    }
    setTxHash('')
  }, [history, onFieldAInput, txHash])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)
  //add test

  const pendingText = (<>Supplying {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)} {currencies[Field.CURRENCY_A]?.symbol} and{' '}
  {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)} {currencies[Field.CURRENCY_B]?.symbol}</>);

  const toggleWalletModal = useWalletModalToggle()
  const showConnectAWallet = Boolean(!account)

  const onUserInputA = (e) => {
    const decimal = currencies[Field.CURRENCY_A]?.decimals || 18;
    const removed = removeUnneededCharacter(e.toString())
    const splited = removed.toString().split('.')
    if (splited.length > 1) {
      setInvalidA(splited[1].length > decimal)
    } else {
      setInvalidA(false)
    }
    onFieldAInput(removed.toString())
  }

  const onUserInputB = (e) => {
    const decimal = currencies[Field.CURRENCY_B]?.decimals || 18;
    const removed = removeUnneededCharacter(e.toString())
    const splited = removed.toString().split('.')
    if (splited.length > 1) {
      setInvalidB(splited[1].length > decimal)
    } else {
      setInvalidB(false)
    }
    onFieldBInput(removed.toString())
  }

  const removeUnneededCharacter = (str: string) => {
    if (str.startsWith('0.')) {
      return str
    } else if (str.startsWith('00')) {
      return str.substring(2)
    } else if (str.length > 1 && str.startsWith('0')) {
      return str.substring(1)
    }
    return str
  }

  return (
    <>
      <ScrollablePage>
        <TransactionConfirmationModal
          isOpen={showConfirm}
          onDismiss={handleDismissConfirmation}
          attemptingTxn={attemptingTxn}
          hash={txHash}
          content={() => (
            <ConfirmationModalContent
              title={""}
              onDismiss={handleDismissConfirmation}
              bottomContent={() => (
                <button className='view-etherscan'>
                  <p>
                    View on Block Explorer
                  </p>
                </button>
              )}
            />
          )}
          pendingText={pendingText}
        />

        <div className='add-liquidity-page'>
          <div className='remove-tab-warrap'>
            <PageWrapper wide={!hasExistingPosition}>
              <HeaderTabs
                creating={false}
                adding={true}
                migrate={false}
                positionID={tokenId}
                defaultSlippage={DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE}
                showBackLink={!hasExistingPosition}
              >
              </HeaderTabs>
              <Wrapper className='remove-tab-content' style={{marginBottom: 0}}>
                <ResponsiveTwoColumns wide={!hasExistingPosition} style={{ display: "block" }} className="remove-tab-content-inner">
                  <div>
                    <DynamicSection
                      disabled={false}
                    >
                      <AutoColumn gap="md">
                        <ThemedText.Label style={{ fontSize: "16px", color: "white", fontWeight:'400px',marginBottom:'5px',height:'24px' }}>
                          Capital
                        </ThemedText.Label>
                        <div className='fromToken' style={{ display: "flex", justifyContent: "space-between" }}>
                          <CurrencyInputPanelLimited
                            value={formattedAmounts[Field.CURRENCY_A]}
                            onUserInput={onUserInputA}
                            onMax={() => {
                              onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact(6) ?? '')
                            }}
                            showMaxButton={false}
                            onCurrencySelect={handleCurrencyASelect}
                            currency={currencies[Field.CURRENCY_A] ?? null}
                            id="add-liquidity-input-tokena"
                            fiatValue={usdcValues[Field.CURRENCY_A]}
                            disabled={waiting || approval === ApprovalState.PENDING}
                            showCommonBases
                          />
                        </div>
                        <ThemedText.Label  style={{ fontSize: "16px", color: "white", fontWeight:'400px',marginBottom:'5px',height:'24px' }}>
                          Token
                        </ThemedText.Label>
                        <div className='toToken' style={{ display: "flex", justifyContent: "space-between" }}>
                          <CurrencyInputPanelAsset
                            value={formattedAmounts[Field.CURRENCY_B]}
                            onUserInput={onUserInputB}
                            onMax={() => {
                              onFieldBInput(assetMax?.toExact(6) ?? '')
                            }}
                            showMaxButton={false}
                            fiatValue={usdcValues[Field.CURRENCY_B]}
                            currency={currencies[Field.CURRENCY_B] ?? null}
                            onCurrencySelect={handleCurrencyBSelect}
                            id="add-liquidity-input-tokenb"
                            disabled={waiting || approval === ApprovalState.PENDING}
                            showCommonBases
                          />
                        </div>
                      </AutoColumn>
                    </DynamicSection>
                  </div>
                </ResponsiveTwoColumns>
              </Wrapper>
              {showConnectAWallet ? (
                <div className='add-liquidity-warrap'>
                  <button className='add-liquidity' style={{ border: "0px" }} onClick={toggleWalletModal}><p>Connect Wallet</p></button>
                </div>
              ):(
                error || ( totalSupply?.quotient.toString() === "0")
                  ?
                    <div className='add-liquidity-warrap' style={{opacity:'0.45'}}>
                      <button className='add-liquidity' style={{border:'0px',cursor: 'not-allowed'}} >
                        <p>{(totalSupply?.quotient.toString() === "0") ? "Invalid Pair" : error }</p>
                      </button>
                    </div>
                  :
                  (
                    (invalidA || invalidB)
                      ?
                      <div className='add-liquidity-footer'>
                        <button className={(invalidA || invalidB) ? 'primary-button-invalid': 'primary-button'} disabled={true}>
                          <p>{(invalidA || invalidB) ? 'Invalid Amount' : 'Insufficient Balance'}</p>
                        </button>
                      </div>
                      :
                      <div className='add-liquidity-footer'>
                        {
                          approval === ApprovalState.NOT_APPROVED && !waiting
                            ?
                            <button className='Approve-pair' style={{ border: "0px" }} onClick={approveToken} >Approve</button>
                            :
                            (
                              (approval === ApprovalState.PENDING || waiting) && (approval !== ApprovalState.APPROVED)
                                ?
                                <button className='primary-button' disabled={true}>
                                  <p style={{ color: "white" }}>Approving...</p>
                                </button>
                                :
                                (
                                  supplyAllowance.gt(capitalInUSD) ?
                                      <div className='add-liquidity-warrap' style={waiting ? { opacity: 0.5 }: {}}>
                                    <button className='add-liquidity' style={!waiting?{ border: "0px" }:{border:'0px',cursor: 'not-allowed',opacity:'0.45'}} onClick={onAdd}>
                                      <p>{waiting?'Transaction in progress-Please wait':'Add Liquidity'}</p>
                                    </button>
                                  </div>
                                  :
                                  <div className='add-liquidity-warrap' style={waiting ? { opacity: 0.5 }: {}}>
                                    <button className='primary-button-invalid' disabled={true}>
                                      <p>{'Check your allowance from DDC tab'}</p>
                                    </button>
                                  </div>
                                )
                            )
                        }
                      </div>
                  )
              )
              }
            </PageWrapper>
          </div>
        </div>
        {addIsUnsupported && (
          <UnsupportedCurrencyFooter
            show={addIsUnsupported}
            currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
          />
        )}
      </ScrollablePage>
    </>
  )
}
